// Passkey (WebAuthn) sign-in backed by Cognito's native WEB_AUTHN factor.
// Part of the runtime `signInModes` flag: callers pass whether it is on.
import {
  CompleteWebAuthnRegistrationCommand,
  DeleteWebAuthnCredentialCommand,
  ListWebAuthnCredentialsCommand,
  StartWebAuthnRegistrationCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoClient } from "../aws/cognitoClient";

// Per-device opt-in: { [normalisedEmail]: { credentialId, createdAt } }. Holds no secrets.
export const PASSKEY_OPT_IN_KEY = "cocoPortal.passkeyOptIn";

export const isPasskeySupported = () =>
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential === "function" &&
  typeof window.navigator?.credentials?.create === "function" &&
  typeof window.navigator?.credentials?.get === "function";

export const canUsePasskeys = (enabled) =>
  enabled === true && isPasskeySupported();

export const isPasskeyCancelledError = (e) =>
  e?.name === "NotAllowedError" || e?.name === "AbortError";

// The authenticator already holds a passkey for this account (excludeCredentials matched).
export const isPasskeyAlreadyRegisteredError = (e) => e?.name === "InvalidStateError";

export const base64UrlToBuffer = (value) => {
  const base64 = String(value).replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export const bufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const toObject = (value) =>
  typeof value === "string" ? JSON.parse(value) : value;

const decodeDescriptors = (descriptors) =>
  (descriptors || []).map((d) => ({ ...d, id: base64UrlToBuffer(d.id) }));

export const parseCreationOptions = (json) => {
  const options = toObject(json);
  return {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    user: { ...options.user, id: base64UrlToBuffer(options.user.id) },
    excludeCredentials: decodeDescriptors(options.excludeCredentials),
  };
};

export const parseRequestOptions = (json) => {
  const options = toObject(json);
  return {
    ...options,
    challenge: base64UrlToBuffer(options.challenge),
    allowCredentials: decodeDescriptors(options.allowCredentials),
  };
};

const commonCredentialJson = (credential) => ({
  id: credential.id,
  rawId: bufferToBase64Url(credential.rawId),
  type: credential.type,
  authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
  clientExtensionResults: credential.getClientExtensionResults?.() || {},
});

export const serializeRegistration = (credential) => {
  if (typeof credential.toJSON === "function") return credential.toJSON();
  const { response } = credential;
  return {
    ...commonCredentialJson(credential),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports: response.getTransports?.() || [],
    },
  };
};

export const serializeAssertion = (credential) => {
  if (typeof credential.toJSON === "function") return credential.toJSON();
  const { response } = credential;
  return {
    ...commonCredentialJson(credential),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64Url(response.userHandle)
        : undefined,
    },
  };
};

// Sign-in: turn Cognito's CREDENTIAL_REQUEST_OPTIONS into the CREDENTIAL challenge answer.
export const getPasskeyAssertion = async (requestOptionsJson) => {
  const credential = await window.navigator.credentials.get({
    publicKey: parseRequestOptions(requestOptionsJson),
  });
  if (!credential) throw new Error("No passkey returned");
  return JSON.stringify(serializeAssertion(credential));
};

export const startPasskeyRegistration = async (accessToken) => {
  const resp = await cognitoClient.send(
    new StartWebAuthnRegistrationCommand({ AccessToken: accessToken }),
  );
  return resp.CredentialCreationOptions;
};

// creationOptions should be fetched beforehand so create() runs inside the user's tap
// (Safari drops the user activation if a network call happens first).
// Returns the new credential id (base64url), which is also Cognito's CredentialId.
export const completePasskeyRegistration = async (accessToken, creationOptions) => {
  const credential = await window.navigator.credentials.create({
    publicKey: parseCreationOptions(creationOptions),
  });
  if (!credential) throw new Error("No passkey created");
  await cognitoClient.send(
    new CompleteWebAuthnRegistrationCommand({
      AccessToken: accessToken,
      Credential: serializeRegistration(credential),
    }),
  );
  return credential.id || bufferToBase64Url(credential.rawId);
};

export const listPasskeys = async (accessToken) => {
  const resp = await cognitoClient.send(
    new ListWebAuthnCredentialsCommand({ AccessToken: accessToken, MaxResults: 20 }),
  );
  return resp.Credentials || [];
};

export const deletePasskey = async (accessToken, credentialId) => {
  await cognitoClient.send(
    new DeleteWebAuthnCredentialCommand({
      AccessToken: accessToken,
      CredentialId: credentialId,
    }),
  );
};

// Cognito and the browser may encode the same credential id as base64 or base64url.
const normaliseCredentialId = (id) =>
  String(id ?? "").replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export const isSameCredential = (a, b) =>
  Boolean(a) && Boolean(b) && normaliseCredentialId(a) === normaliseCredentialId(b);

const normaliseEmail = (email) => String(email ?? "").trim().toLowerCase();

const readOptIns = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(PASSKEY_OPT_IN_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeOptIns = (optIns) => {
  try {
    if (Object.keys(optIns).length) {
      localStorage.setItem(PASSKEY_OPT_IN_KEY, JSON.stringify(optIns));
    } else {
      localStorage.removeItem(PASSKEY_OPT_IN_KEY);
    }
  } catch {
    // Storage may be unavailable (private mode); passkey sign-in just won't be offered.
  }
};

export const getPasskeyOptIn = (email) => {
  const key = normaliseEmail(email);
  return key ? readOptIns()[key] || null : null;
};

export const hasPasskeyOptIn = (email) => Boolean(getPasskeyOptIn(email));

export const savePasskeyOptIn = (email, credentialId) => {
  const key = normaliseEmail(email);
  if (!key) return;
  writeOptIns({
    ...readOptIns(),
    [key]: { credentialId: credentialId || null, createdAt: new Date().toISOString() },
  });
};

export const clearPasskeyOptIn = (email) => {
  const key = normaliseEmail(email);
  if (!key) return;
  const optIns = readOptIns();
  delete optIns[key];
  writeOptIns(optIns);
};
